/**
 * UiHeading Composable
 *
 * Manages WCAG-compliant heading hierarchy using provide/inject.
 * No global state, no mutations during render — fully SSR-safe and deterministic.
 *
 * Two heading levels are tracked independently:
 * - SEO level: drives the rendered <h1>-<h6> tag. Capped at 6 because HTML
 *   has no <h7>+; deeply nested sections keep reusing <h6>.
 * - WCAG level: drives role="heading" + aria-level for non-heading elements.
 *   Not capped — aria-level may exceed 6, so deep nesting stays semantically
 *   correct for assistive technology even once the SEO level is pinned at 6.
 *
 * How it works:
 * - The layout provides default levels (1) via provideHeadingContext()
 * - <UiHeading> reads the injected levels and renders the correct tag/aria-level
 * - <UiHeadingSection> provides level+1 to all its descendants
 * - Header/Footer provide 'div' via inject('ui-heading-seo-level') to suppress headings
 * - Pages with explicit tags use force-tag="div"
 */

import type { HeadingLevel } from '~layers/types/heading'

// Injection key for the SEO heading level (drives the <h1>-<h6> tag, capped at 6)
export const HEADING_SEO_LEVEL_KEY = Symbol('ui-heading-seo-level') as InjectionKey<HeadingLevel>

// Injection key for the WCAG heading level (drives aria-level, not capped)
export const HEADING_WCAG_LEVEL_KEY = Symbol('ui-heading-wcag-level') as InjectionKey<number>

/**
 * Provide a heading level context.
 * Call this in layouts to set the default heading level.
 *
 * @param level - The heading level to start both the SEO and WCAG levels at (default: 1)
 */
export function provideHeadingContext(level: HeadingLevel = 1): void {
  provide(HEADING_SEO_LEVEL_KEY, level)
  provide(HEADING_WCAG_LEVEL_KEY, level)
}

/**
 * Read the current heading levels from the component tree.
 * Used internally by <UiHeading>.
 *
 * @returns seoLevel (1-6, for the <hN> tag) and wcagLevel (>=1, for aria-level)
 */
export function useUiHeading() {
  const seoLevel = inject(HEADING_SEO_LEVEL_KEY, 1 as HeadingLevel)
  const wcagLevel = inject(HEADING_WCAG_LEVEL_KEY, 1)

  return { seoLevel, wcagLevel }
}

/**
 * Provide a heading context one level deeper than the parent.
 * Used internally by <UiHeadingSection>.
 *
 * The SEO level is capped at 6 (no <h7>), while the WCAG level keeps
 * incrementing so assistive technology sees the true nesting depth.
 */
export function provideHeadingSectionContext(): void {
  const parentSeoLevel = inject(HEADING_SEO_LEVEL_KEY, 1 as HeadingLevel)
  const parentWcagLevel = inject(HEADING_WCAG_LEVEL_KEY, 1)

  const childSeoLevel = Math.min(parentSeoLevel + 1, 6) as HeadingLevel
  const childWcagLevel = parentWcagLevel + 1

  provide(HEADING_SEO_LEVEL_KEY, childSeoLevel)
  provide(HEADING_WCAG_LEVEL_KEY, childWcagLevel)
}
